import { ValidatorSchema, Validator } from '@libs/utilities/validator';
import { Util } from '@libs/utilities/util';
import { EnumAiModel } from '@domains/ai-toolkit/shared';

import { FlyingAiTemplateSchema, IFlyingAiTemplate } from './ai-template.interface';
import { IDisplateText, IGlobalText, IMbaMarketplace, IMbaProduct, IMbaText } from '../data-structures';
import { DEPRECATED_PRODUCTS, EnumDisplateArtworkType, EnumDisplateCategory, EnumGlobalColorOption, EnumGlobalTypeOption, EnumMbaColor, EnumMbaFitType, EnumMbaMarketplace, EnumMbaPrint, EnumMbaProduct } from '../app-constants';


/**------------------------------------------------------
 * Configurations
 */
export const FLYING_GENERATE_LISTING_LIMITS: IFlyingGenerateListingConfig = {
	FLY_ID_LENGTH		: 120,
	MAX_KEYWORDS		: 100,
	KEYWORD_LENGTH		: 120,
	TOPIC_LENGTH		: 250,
	INSTRUCTIONS_LENGTH	: 250,
	MAX_LIST_SIZE		: 500,
	GLOBAL_DEFAULTS		: {				// these are the defaults used to generate text length for global
		MAX_TITLE		: 120,
		MAX_DESCRIPTION : 500,
		MAX_KEYWORDS 	: 50
	},
	MBA_DEFAULTS		: {				// these are the defaults used to generate text length for mba
		MAX_BRAND		: 50,
		MAX_TITLE		: 60,
		MAX_BULLET1 	: 240,
		MAX_BULLET2 	: 240,
		MAX_DESCRIPTION : 500
	},
	AI_MODELS			: {
		VISION			: [EnumAiModel.Gpt6_1Terra, EnumAiModel.Gpt6Luna, EnumAiModel.Gpt5_6Luna, EnumAiModel.Gpt5_4Mini],	// order defines the default model
		LISTING			: [EnumAiModel.Gpt6_1Terra, EnumAiModel.Gpt6Luna, EnumAiModel.Gpt5_6Luna, EnumAiModel.Gpt5_4Mini]
	}
};

interface IFlyingGenerateListingConfig {
	FLY_ID_LENGTH		: number;
	MAX_KEYWORDS		: number;
	KEYWORD_LENGTH		: number;
	TOPIC_LENGTH		: number;
	INSTRUCTIONS_LENGTH	: number;
	MAX_LIST_SIZE		: number;
	GLOBAL_DEFAULTS		: {				// these are the defaults used to generate text length for global
		MAX_TITLE		: number;
		MAX_DESCRIPTION : number;
		MAX_KEYWORDS 	: number;
	};
	MBA_DEFAULTS		: {				// these are the defaults used to generate text length for mba
		MAX_BRAND		: number;
		MAX_TITLE		: number;
		MAX_BULLET1 	: number;
		MAX_BULLET2 	: number;
		MAX_DESCRIPTION : number;
	};
	AI_MODELS			: {
		VISION			: EnumAiModel[];
		LISTING			: EnumAiModel[];
	};
}


/**------------------------------------------------------
 * Requests Vision
 */
export interface IFlyingGenerateByVisionRequest {
	model			: EnumAiModel;
	entries			: IFlyingDirectVisionParams[];
	setupPlatforms?: EnumFlyingPodSetupPlatform[];
}

export enum EnumFlyingPodSetupPlatform {
	Global		= 'global',
	MBA			= 'mba',
	Displate	= 'displate'
}

export enum EnumFlyingPodSetupStrictness {
	Strict	= 'strict',
	Medium	= 'medium',
	Loose	= 'loose'
}

export interface IFlyingLocalListingImage {
	mimeType: 'image/png' | 'image/jpeg';
	data: string;
	colorType?: 'light' | 'dark';
}

export interface IFlyingDirectVisionParams extends IFlyingGenerateByVisionParams {
	localImage?: IFlyingLocalListingImage;
}

export interface IFlyingGenerateByVisionParams {
	flyId			: string;
	topic			: string;
	instructions	: string;			// custom instructions
}


/**------------------------------------------------------
 * Request MBA Setup By Vision
 */
export interface IFlyingMbaSetupRequest {
	model			: EnumAiModel;
	flyId			: string;
}

export interface IFlyingMbaSetupResponse {
	flyId			: string;
	products		: IMbaProduct[];
	confidence		: number;
	reason			: string;
	warnings		: string[];
}

export interface IFlyingPodSetupResponse {
	flyId			: string;
	global			: {
		types		: EnumGlobalTypeOption[];
		color		: EnumGlobalColorOption;
	};
	mba				: IFlyingMbaSetupResponse;
	displate		: {
		categories	: EnumDisplateCategory[];
		background	: string;
		artworkType	: EnumDisplateArtworkType;
	};
	confidence		: number;
	reason			: string;
	warnings		: string[];
}

export enum EnumFlyingListingStreamEventType {
	Progress	= 'progress',
	Result		= 'result'
}

export enum EnumFlyingListingProgressStage {
	Preparing	= 'preparing',
	Generating	= 'generating'
}

const ACTIVE_MBA_PRODUCT_CODES: EnumMbaProduct[] = Object.values(EnumMbaProduct)
	.filter((productCode: EnumMbaProduct) => !DEPRECATED_PRODUCTS.includes(productCode));

export interface IFlyingListingProgressEvent {
	type		: EnumFlyingListingStreamEventType.Progress;
	stage		: EnumFlyingListingProgressStage;
	current		: number;
	total		: number;
	flyId		: string;
}

export interface IFlyingListingResultEvent {
	type		: EnumFlyingListingStreamEventType.Result;
	current		: number;
	total		: number;
	listing		: IFlyingGeneratedListingResponse;
	setup		?: IFlyingPodSetupResponse;
	creditsUsed	?: number;
	creditBalanceAfter?: number;
}

export interface IFlyingListingFailureEvent {
	code		?: 'NOT_STARTED';
	flyId		: string;
	current		: number;
	total		: number;
	message		: string;
}

export type TypeFlyingListingStreamEvent = IFlyingListingProgressEvent | IFlyingListingResultEvent;
export type TypeFlyingListingVisionStreamEvent = TypeFlyingListingStreamEvent | IFlyingListingFailureEvent;
export type TypeFlyingListingBatchStreamEvent = IFlyingGeneratedListingResponse | IFlyingListingFailureEvent;


/**------------------------------------------------------
 * Request Keywords
 */
export interface IFlyingGenerateByKeywordsRequest {
	model			: EnumAiModel;
	entries			: IFlyingGenerateByKeywordsParams[];
}

export interface IFlyingGenerateByKeywordsParams {
	flyId			: string;
	mainKeywords	: string[];
	fillKeywords	: string[];
}


/**------------------------------------------------------
 * Request Template
 */
export interface IFlyingGenerateByTemplateRequest {
	model			: EnumAiModel;
	entries			: IFlyingGenerateByTemplateParams[];
}

export interface IFlyingGenerateByTemplateParams {
	flyId			: string;
	template		: IFlyingAiTemplate;
	instructions	: string;			// custom instructions
}


/**------------------------------------------------------
 * FLAI Agent
 * -------------------
 * FLAI keeps the listing-generation configuration selected by the user,
 * then combines it with the requested POD platform setup.
 */
export enum EnumFlyingListingGenerationType {
	Vision		= 'vision',
	Keywords	= 'keywords',
	Template	= 'template'
}

export interface IFlyingFlaiAgentRequest {
	model			: EnumAiModel;
	setupPlatforms	: EnumFlyingPodSetupPlatform[];
	setupStrictness?: EnumFlyingPodSetupStrictness;	// defaults to strict for backwards compatibility
	generateListings?: boolean;				// defaults to true; setup-only workflows explicitly disable listing generation
	mbaProducts	   ?: EnumMbaProduct[];		// omitted for backwards compatibility; the server then evaluates every active product
	entries			: TypeFlyingFlaiAgentParams[];
}

export type TypeFlyingFlaiAgentGenerationConfig = Omit<IFlyingFlaiAgentRequest, 'setupPlatforms'>;

export type TypeFlyingFlaiAgentParams =
	| IFlyingFlaiAgentVisionParams
	| IFlyingFlaiAgentKeywordsParams
	| IFlyingFlaiAgentTemplateParams;

type TypeFlyingFlaiAgentCandidate = TypeFlyingFlaiAgentParams & Partial<
	IFlyingGenerateByVisionParams
	& IFlyingGenerateByKeywordsParams
	& IFlyingGenerateByTemplateParams
>;

export interface IFlyingFlaiAgentVisionParams extends IFlyingGenerateByVisionParams {
	generationType: EnumFlyingListingGenerationType.Vision;
}

export interface IFlyingFlaiAgentKeywordsParams extends IFlyingGenerateByKeywordsParams {
	generationType: EnumFlyingListingGenerationType.Keywords;
}

export interface IFlyingFlaiAgentTemplateParams extends IFlyingGenerateByTemplateParams {
	generationType: EnumFlyingListingGenerationType.Template;
}


/**------------------------------------------------------
 * Response
 */
export interface IFlyingGeneratedListingResponse {
	flyId			: string;
	global 	 		: IGlobalText[]	| null;
	mba  	 		: IMbaText[]	| null;
	displate 		: IDisplateText	| null;
}


/**------------------------------------------------------
 * Schemas
 */
export class FlyingGenerateByVisionRequestSchema implements ValidatorSchema<IFlyingGenerateByVisionRequest> {
	model	 		= Validator.Enum(EnumAiModel).isWhitelisted(FLYING_GENERATE_LISTING_LIMITS.AI_MODELS.VISION).isNotEmpty();
	setupPlatforms 	= Validator.Array().isNotEmpty().isMaxSize(3).each(Validator.Enum(EnumFlyingPodSetupPlatform).isNotEmpty()).optional();
	entries			= Validator.Array().isNotEmpty().isMaxSize(FLYING_GENERATE_LISTING_LIMITS.MAX_LIST_SIZE).inspectEach<IFlyingDirectVisionParams>({
		localImage: Validator.Object().inspect<IFlyingLocalListingImage>({
			mimeType: Validator.String().isWhitelisted(['image/png', 'image/jpeg']).isNotEmpty(),
			data: Validator.String().isNotEmpty().isMaxLength(699052),
			colorType: Validator.String().isWhitelisted(['light', 'dark']).optional()
		}).optional(),
		flyId			: Validator.String().isNotEmpty().isMaxLength(FLYING_GENERATE_LISTING_LIMITS.FLY_ID_LENGTH),
		topic			: Validator.String().isMaxLength(FLYING_GENERATE_LISTING_LIMITS.TOPIC_LENGTH).hasNoHtmlScripts(),
		instructions	: Validator.String().isMaxLength(FLYING_GENERATE_LISTING_LIMITS.INSTRUCTIONS_LENGTH).hasNoHtmlScripts()
	});
}

export class FlyingMbaSetupRequestSchema implements ValidatorSchema<IFlyingMbaSetupRequest> {
	model		= Validator.Enum(EnumAiModel).isWhitelisted(FLYING_GENERATE_LISTING_LIMITS.AI_MODELS.VISION).isNotEmpty();
	flyId		= Validator.String().isNotEmpty().isMaxLength(FLYING_GENERATE_LISTING_LIMITS.FLY_ID_LENGTH);
}

export class FlyingMbaSetupResponseSchema implements ValidatorSchema<IFlyingMbaSetupResponse> {
	flyId		= Validator.String().isNotEmpty().isMaxLength(FLYING_GENERATE_LISTING_LIMITS.FLY_ID_LENGTH);
	products	= Validator.Array().inspectEach<IMbaProduct>({
		productCode	: Validator.Enum(EnumMbaProduct).isNotEmpty(),
		marketplaces	: Validator.Array().isNotEmpty().inspectEach<IMbaMarketplace>({
			market	: Validator.Enum(EnumMbaMarketplace).isNotEmpty(),
			price	: Validator.Number().isPositive()
		}),
		fitTypes	: Validator.Array().each(Validator.Enum(EnumMbaFitType).isNotEmpty()),
		print		: Validator.Enum(EnumMbaPrint).isNotEmpty(),
		colors		: Validator.Array().each(Validator.Enum(EnumMbaColor).isNotEmpty()),
		background	: Validator.String().optional()
	});
	confidence	= Validator.Number().isGreaterThanOrEqual(0).isLessThanOrEqual(100);
	reason		= Validator.String().isNotEmpty().isMaxLength(300);
	warnings	= Validator.Array().each(Validator.String().isNotEmpty().isMaxLength(300));
}

export class FlyingGenerateByKeywordsRequestSchema implements ValidatorSchema<IFlyingGenerateByKeywordsRequest> {
	model 		= Validator.Enum(EnumAiModel).isWhitelisted(FLYING_GENERATE_LISTING_LIMITS.AI_MODELS.LISTING).isNotEmpty();
	entries		= Validator.Array().isNotEmpty().isMaxSize(FLYING_GENERATE_LISTING_LIMITS.MAX_LIST_SIZE).inspectEach<IFlyingGenerateByKeywordsParams>({
		flyId		: Validator.String().isNotEmpty().isMaxLength(FLYING_GENERATE_LISTING_LIMITS.FLY_ID_LENGTH),
		mainKeywords: Validator.Array().isNotEmpty().isMaxSize(FLYING_GENERATE_LISTING_LIMITS.MAX_KEYWORDS)
						.each(Validator.String().isNotEmpty().isMaxLength(FLYING_GENERATE_LISTING_LIMITS.KEYWORD_LENGTH)
						.hasNoHtmlScripts().toLowerCase().trim()).isNotEmpty(),
		fillKeywords: Validator.Array().isMaxSize(FLYING_GENERATE_LISTING_LIMITS.MAX_KEYWORDS)
						.each(Validator.String().isNotEmpty().isMaxLength(FLYING_GENERATE_LISTING_LIMITS.KEYWORD_LENGTH)
						.hasNoHtmlScripts().toLowerCase().trim())
	});
}

export class FlyingGenerateByTemplateRequestSchema implements ValidatorSchema<IFlyingGenerateByTemplateRequest> {
	model	 	= Validator.Enum(EnumAiModel).isWhitelisted(FLYING_GENERATE_LISTING_LIMITS.AI_MODELS.LISTING).isNotEmpty();
	entries		= Validator.Array().isNotEmpty().isMaxSize(FLYING_GENERATE_LISTING_LIMITS.MAX_LIST_SIZE).inspectEach<IFlyingGenerateByTemplateParams>({
		flyId		: Validator.String().isNotEmpty().isMaxLength(FLYING_GENERATE_LISTING_LIMITS.FLY_ID_LENGTH),
		template	: Validator.Object().inspect<IFlyingAiTemplate>(FlyingAiTemplateSchema),
		instructions: Validator.String().isMaxLength(FLYING_GENERATE_LISTING_LIMITS.INSTRUCTIONS_LENGTH).hasNoHtmlScripts()
	});
}

export class FlyingFlaiAgentRequestSchema implements ValidatorSchema<IFlyingFlaiAgentRequest> {
	model 			= Validator.Enum(EnumAiModel).isWhitelisted([
		...FLYING_GENERATE_LISTING_LIMITS.AI_MODELS.VISION,
		...FLYING_GENERATE_LISTING_LIMITS.AI_MODELS.LISTING
	]).isNotEmpty();
	setupPlatforms = Validator.Array().isNotEmpty().isMaxSize(3).each(Validator.Enum(EnumFlyingPodSetupPlatform).isNotEmpty());
	setupStrictness = Validator.Enum(EnumFlyingPodSetupStrictness).optional();
	generateListings = Validator.Boolean().optional();
	mbaProducts	 = Validator.Array().isNotEmpty().isMaxSize(ACTIVE_MBA_PRODUCT_CODES.length)
		.each(Validator.Enum(EnumMbaProduct).isWhitelisted(ACTIVE_MBA_PRODUCT_CODES).isNotEmpty()).optional();
	entries			= Validator.Array()
		.isNotEmpty()
		.isMaxSize(FLYING_GENERATE_LISTING_LIMITS.MAX_LIST_SIZE)
		.inspectEach<TypeFlyingFlaiAgentParams>({
			generationType: Validator.Enum(EnumFlyingListingGenerationType).isNotEmpty(),
			flyId			: Validator.String().isNotEmpty().isMaxLength(FLYING_GENERATE_LISTING_LIMITS.FLY_ID_LENGTH),
			topic			: Validator.String().isMaxLength(FLYING_GENERATE_LISTING_LIMITS.TOPIC_LENGTH).hasNoHtmlScripts().optional(),
			instructions	: Validator.String().isMaxLength(FLYING_GENERATE_LISTING_LIMITS.INSTRUCTIONS_LENGTH).hasNoHtmlScripts().optional(),
			mainKeywords	: Validator.Array().isNotEmpty().isMaxSize(FLYING_GENERATE_LISTING_LIMITS.MAX_KEYWORDS)
				.each(Validator.String().isNotEmpty().isMaxLength(FLYING_GENERATE_LISTING_LIMITS.KEYWORD_LENGTH).hasNoHtmlScripts().toLowerCase().trim()).optional(),
			fillKeywords	: Validator.Array().isMaxSize(FLYING_GENERATE_LISTING_LIMITS.MAX_KEYWORDS)
				.each(Validator.String().isNotEmpty().isMaxLength(FLYING_GENERATE_LISTING_LIMITS.KEYWORD_LENGTH).hasNoHtmlScripts().toLowerCase().trim()).optional(),
			template		: Validator.Object().inspect<IFlyingAiTemplate>(FlyingAiTemplateSchema).optional()
		})
		.verify<TypeFlyingFlaiAgentParams[]>((entries: TypeFlyingFlaiAgentParams[]) => ({
			isValid: entries.every((entry: TypeFlyingFlaiAgentParams) => {
				const candidate: TypeFlyingFlaiAgentCandidate = entry;
				switch (candidate.generationType) {
					case EnumFlyingListingGenerationType.Vision:
						return Util.Basic.isDefined(candidate.topic)
							&& Util.Basic.isDefined(candidate.instructions)
							&& Util.Basic.isUndefined(candidate.mainKeywords)
							&& Util.Basic.isUndefined(candidate.fillKeywords)
							&& Util.Basic.isUndefined(candidate.template);
					case EnumFlyingListingGenerationType.Keywords:
						return Util.Array.isNotEmpty(candidate.mainKeywords)
							&& Util.Basic.isDefined(candidate.fillKeywords)
							&& Util.Basic.isUndefined(candidate.topic)
							&& Util.Basic.isUndefined(candidate.instructions)
							&& Util.Basic.isUndefined(candidate.template);
					case EnumFlyingListingGenerationType.Template:
						return Util.Basic.isDefined(candidate.template)
							&& Util.Basic.isDefined(candidate.instructions)
							&& Util.Basic.isUndefined(candidate.topic)
							&& Util.Basic.isUndefined(candidate.mainKeywords)
							&& Util.Basic.isUndefined(candidate.fillKeywords);
					default:
						return false;
				}
			}),
			error: 'FLAI entries do not match their selected listing generation type'
		}));
}

export class FlyingGeneratedListingResponseSchema implements ValidatorSchema<IFlyingGeneratedListingResponse> {
	flyId		= Validator.String().isNotEmpty();
	global 	 	= Validator.Array().optional();
	mba  	 	= Validator.Array().optional();
	displate 	= Validator.Object().optional();
}
